import type { SimDefinition, SimDifficulty, SimSubject } from "./types";

export type SimMeta = {
  id: string;
  title: string;
  chapter: string;
  subject: SimSubject;
  difficulty: SimDifficulty;
  minutes: number;
  blurb: string;
  /** tailwind gradient classes used for the thumbnail */
  gradient: string;
  popularity: number;
  tags: string[];
  load: () => Promise<{ default: SimDefinition }>;
};

/** Metadata only — simulation code is dynamically imported when a sim is opened. */
export const SIMULATIONS: SimMeta[] = [
  {
    id: "projectile-motion",
    title: "Projectile Motion",
    chapter: "Motion in a Plane",
    subject: "Physics",
    difficulty: "Beginner",
    minutes: 6,
    blurb: "Launch angle, speed and gravity shape the parabola in real time.",
    gradient: "from-indigo-500/70 via-violet-500/45 to-sky-400/30",
    popularity: 98,
    tags: ["kinematics", "range", "parabola", "trajectory"],
    load: () => import("./sims/projectile-motion"),
  },
  {
    id: "simple-harmonic",
    title: "Simple Harmonic Motion",
    chapter: "Oscillations",
    subject: "Physics",
    difficulty: "Intermediate",
    minutes: 8,
    blurb: "Spring–mass oscillator with live displacement and energy split.",
    gradient: "from-sky-500/70 via-cyan-400/45 to-emerald-400/30",
    popularity: 92,
    tags: ["shm", "spring", "period", "energy"],
    load: () => import("./sims/simple-harmonic"),
  },
  {
    id: "wave-superposition",
    title: "Wave Superposition",
    chapter: "Waves",
    subject: "Physics",
    difficulty: "Advanced",
    minutes: 9,
    blurb: "Interfere two travelling waves and watch beats and standing patterns form.",
    gradient: "from-fuchsia-500/70 via-purple-500/45 to-indigo-400/30",
    popularity: 84,
    tags: ["interference", "beats", "standing wave", "phase"],
    load: () => import("./sims/wave-superposition"),
  },
  {
    id: "gas-laws",
    title: "Ideal Gas Laws",
    chapter: "States of Matter",
    subject: "Chemistry",
    difficulty: "Beginner",
    minutes: 7,
    blurb: "Compress a piston and read pressure straight off the PV isotherm.",
    gradient: "from-emerald-500/70 via-teal-400/45 to-sky-400/30",
    popularity: 88,
    tags: ["boyle", "charles", "pv=nrt", "isotherm"],
    load: () => import("./sims/gas-laws"),
  },
  {
    id: "bohr-atom",
    title: "Bohr Atom & Spectra",
    chapter: "Atomic Structure",
    subject: "Chemistry",
    difficulty: "Intermediate",
    minutes: 8,
    blurb: "Move an electron between shells and see the emitted photon energy.",
    gradient: "from-amber-500/70 via-orange-500/45 to-rose-400/30",
    popularity: 90,
    tags: ["hydrogen", "energy levels", "rydberg", "photon"],
    load: () => import("./sims/bohr-atom"),
  },
  {
    id: "conic-explorer",
    title: "Conic Section Explorer",
    chapter: "Conic Sections",
    subject: "Mathematics",
    difficulty: "Intermediate",
    minutes: 7,
    blurb: "Sweep eccentricity from circle to hyperbola and track the focus.",
    gradient: "from-violet-500/70 via-indigo-500/45 to-blue-400/30",
    popularity: 80,
    tags: ["ellipse", "parabola", "hyperbola", "eccentricity"],
    load: () => import("./sims/conic-explorer"),
  },
  {
    id: "sine-transform",
    title: "Trigonometric Transformer",
    chapter: "Trigonometric Functions",
    subject: "Mathematics",
    difficulty: "Beginner",
    minutes: 5,
    blurb: "See amplitude, frequency and phase reshape y = A sin(Bx + C) + D.",
    gradient: "from-rose-500/70 via-pink-500/45 to-violet-400/30",
    popularity: 76,
    tags: ["graph", "amplitude", "phase shift", "period"],
    load: () => import("./sims/sine-transform"),
  },
  {
    id: "derivative-slope",
    title: "Derivative as Slope",
    chapter: "Differentiation",
    subject: "Mathematics",
    difficulty: "Advanced",
    minutes: 8,
    blurb: "Drag the tangent along a cubic and read f'(x) on the live graph.",
    gradient: "from-cyan-500/70 via-blue-500/45 to-indigo-400/30",
    popularity: 72,
    tags: ["tangent", "calculus", "slope", "maxima"],
    load: () => import("./sims/derivative-slope"),
  },
  {
    id: "units-dimensions",
    title: "Units, Dimensions & Errors",
    chapter: "Units and Measurements",
    subject: "Physics",
    difficulty: "Beginner",
    minutes: 6,
    blurb: "Watch how least count and powers inflate the percentage error.",
    gradient: "from-indigo-500/70 via-violet-500/45 to-sky-400/30",
    popularity: 70,
    tags: ["dimensions", "error", "significant figures", "least count"],
    load: () => import("./sims/units-dimensions"),
  },
  {
    id: "motion-graphs",
    title: "Motion Graphs",
    chapter: "Motion in a Straight Line",
    subject: "Physics",
    difficulty: "Beginner",
    minutes: 6,
    blurb: "Link x–t, v–t and a–t with slope and area in real time.",
    gradient: "from-sky-500/70 via-cyan-400/45 to-emerald-400/30",
    popularity: 89,
    tags: ["kinematics", "graphs", "slope", "area"],
    load: () => import("./sims/motion-graphs"),
  },
  {
    id: "vector-addition",
    title: "Vector Addition",
    chapter: "Vectors",
    subject: "Physics",
    difficulty: "Beginner",
    minutes: 5,
    blurb: "Sweep the angle and see the resultant swing between |A−B| and A+B.",
    gradient: "from-fuchsia-500/70 via-purple-500/45 to-indigo-400/30",
    popularity: 74,
    tags: ["vectors", "resultant", "parallelogram"],
    load: () => import("./sims/vector-addition"),
  },
  {
    id: "relative-motion",
    title: "Relative Motion & River Boat",
    chapter: "Motion in a Plane",
    subject: "Physics",
    difficulty: "Intermediate",
    minutes: 7,
    blurb: "Solve crossing time versus drift for any river current.",
    gradient: "from-amber-500/70 via-orange-500/45 to-rose-400/30",
    popularity: 71,
    tags: ["relative velocity", "river boat", "drift"],
    load: () => import("./sims/relative-motion"),
  },
  {
    id: "friction-incline",
    title: "Friction on an Incline",
    chapter: "Laws of Motion",
    subject: "Physics",
    difficulty: "Intermediate",
    minutes: 7,
    blurb: "Find the angle of repose and the sliding acceleration.",
    gradient: "from-emerald-500/70 via-teal-400/45 to-sky-400/30",
    popularity: 86,
    tags: ["friction", "incline", "repose", "normal force"],
    load: () => import("./sims/friction-incline"),
  },
  {
    id: "work-energy-power",
    title: "Work, Energy & Power",
    chapter: "Work, Energy and Power",
    subject: "Physics",
    difficulty: "Beginner",
    minutes: 6,
    blurb: "Track work, kinetic energy and power as a force acts.",
    gradient: "from-cyan-500/70 via-blue-500/45 to-indigo-400/30",
    popularity: 78,
    tags: ["work", "energy theorem", "power"],
    load: () => import("./sims/work-energy-power"),
  },
  {
    id: "circular-motion",
    title: "Circular Motion & Banking",
    chapter: "Circular Motion",
    subject: "Physics",
    difficulty: "Intermediate",
    minutes: 6,
    blurb: "Feel how v²/r drives the centripetal requirement.",
    gradient: "from-indigo-500/70 via-violet-500/45 to-sky-400/30",
    popularity: 81,
    tags: ["centripetal", "banking", "vertical circle"],
    load: () => import("./sims/circular-motion"),
  },
  {
    id: "rotational-motion",
    title: "Rolling & Rotational Dynamics",
    chapter: "Rotational Motion",
    subject: "Physics",
    difficulty: "Advanced",
    minutes: 9,
    blurb: "Race shapes down an incline using their moment of inertia.",
    gradient: "from-sky-500/70 via-cyan-400/45 to-emerald-400/30",
    popularity: 77,
    tags: ["torque", "moment of inertia", "rolling"],
    load: () => import("./sims/rotational-motion"),
  },
  {
    id: "gravitation-orbit",
    title: "Satellite Orbits & Escape",
    chapter: "Gravitation",
    subject: "Physics",
    difficulty: "Intermediate",
    minutes: 8,
    blurb: "Tune altitude and planet mass to get orbital speed and period.",
    gradient: "from-fuchsia-500/70 via-purple-500/45 to-indigo-400/30",
    popularity: 83,
    tags: ["kepler", "orbit", "escape velocity"],
    load: () => import("./sims/gravitation-orbit"),
  },
  {
    id: "fluid-bernoulli",
    title: "Bernoulli & Continuity",
    chapter: "Mechanical Properties of Fluids",
    subject: "Physics",
    difficulty: "Intermediate",
    minutes: 7,
    blurb: "Squeeze a venturi throat and watch the pressure drop.",
    gradient: "from-amber-500/70 via-orange-500/45 to-rose-400/30",
    popularity: 69,
    tags: ["bernoulli", "venturi", "continuity"],
    load: () => import("./sims/fluid-bernoulli"),
  },
  {
    id: "thermal-kinetic-theory",
    title: "Kinetic Theory & Maxwell Speeds",
    chapter: "Kinetic Theory of Gases",
    subject: "Physics",
    difficulty: "Advanced",
    minutes: 8,
    blurb: "Heat the gas and watch the speed distribution stretch.",
    gradient: "from-emerald-500/70 via-teal-400/45 to-sky-400/30",
    popularity: 75,
    tags: ["rms speed", "maxwell", "equipartition"],
    load: () => import("./sims/thermal-kinetic-theory"),
  },
  {
    id: "thermodynamic-processes",
    title: "Thermodynamic Processes",
    chapter: "Thermodynamics",
    subject: "Physics",
    difficulty: "Advanced",
    minutes: 9,
    blurb: "Compare isothermal, adiabatic and isobaric paths on p–V.",
    gradient: "from-cyan-500/70 via-blue-500/45 to-indigo-400/30",
    popularity: 79,
    tags: ["first law", "adiabatic", "carnot", "pv diagram"],
    load: () => import("./sims/thermodynamic-processes"),
  },
  {
    id: "standing-waves",
    title: "Standing Waves & Harmonics",
    chapter: "Waves",
    subject: "Physics",
    difficulty: "Intermediate",
    minutes: 7,
    blurb: "Shape harmonics on a string and read the frequency.",
    gradient: "from-indigo-500/70 via-violet-500/45 to-sky-400/30",
    popularity: 82,
    tags: ["harmonics", "nodes", "resonance", "organ pipe"],
    load: () => import("./sims/standing-waves"),
  },
  {
    id: "doppler-effect",
    title: "Doppler Effect",
    chapter: "Waves",
    subject: "Physics",
    difficulty: "Intermediate",
    minutes: 6,
    blurb: "Move source and observer to shift the perceived pitch.",
    gradient: "from-sky-500/70 via-cyan-400/45 to-emerald-400/30",
    popularity: 73,
    tags: ["doppler", "pitch", "sound"],
    load: () => import("./sims/doppler-effect"),
  },
  {
    id: "electric-field",
    title: "Electric Field & Coulomb",
    chapter: "Electrostatics",
    subject: "Physics",
    difficulty: "Intermediate",
    minutes: 8,
    blurb: "Place two charges and hunt for the null point.",
    gradient: "from-fuchsia-500/70 via-purple-500/45 to-indigo-400/30",
    popularity: 85,
    tags: ["coulomb", "field", "gauss", "null point"],
    load: () => import("./sims/electric-field"),
  },
  {
    id: "capacitors",
    title: "Capacitors & RC Circuits",
    chapter: "Capacitance",
    subject: "Physics",
    difficulty: "Intermediate",
    minutes: 7,
    blurb: "Charge a capacitor and measure the RC time constant.",
    gradient: "from-amber-500/70 via-orange-500/45 to-rose-400/30",
    popularity: 76,
    tags: ["rc", "time constant", "dielectric", "energy"],
    load: () => import("./sims/capacitors"),
  },
  {
    id: "current-electricity",
    title: "Cells, Load & Power Transfer",
    chapter: "Current Electricity",
    subject: "Physics",
    difficulty: "Intermediate",
    minutes: 7,
    blurb: "Find the load that draws maximum power from a real cell.",
    gradient: "from-emerald-500/70 via-teal-400/45 to-sky-400/30",
    popularity: 80,
    tags: ["ohm", "internal resistance", "kirchhoff"],
    load: () => import("./sims/current-electricity"),
  },
  {
    id: "magnetic-force",
    title: "Charges in Magnetic Fields",
    chapter: "Moving Charges and Magnetism",
    subject: "Physics",
    difficulty: "Advanced",
    minutes: 8,
    blurb: "Bend a charged particle into a circle with qv×B.",
    gradient: "from-cyan-500/70 via-blue-500/45 to-indigo-400/30",
    popularity: 78,
    tags: ["lorentz", "cyclotron", "radius"],
    load: () => import("./sims/magnetic-force"),
  },
  {
    id: "faraday-induction",
    title: "Faraday & Lenz Induction",
    chapter: "Electromagnetic Induction",
    subject: "Physics",
    difficulty: "Advanced",
    minutes: 8,
    blurb: "Rotate a coil and watch EMF lag the flux by 90°.",
    gradient: "from-indigo-500/70 via-violet-500/45 to-sky-400/30",
    popularity: 77,
    tags: ["emf", "flux", "lenz", "generator"],
    load: () => import("./sims/faraday-induction"),
  },
  {
    id: "ac-circuits",
    title: "Series LCR Resonance",
    chapter: "Alternating Current",
    subject: "Physics",
    difficulty: "Advanced",
    minutes: 9,
    blurb: "Sweep frequency to find resonance and the Q factor.",
    gradient: "from-sky-500/70 via-cyan-400/45 to-emerald-400/30",
    popularity: 74,
    tags: ["lcr", "resonance", "impedance", "power factor"],
    load: () => import("./sims/ac-circuits"),
  },
  {
    id: "ray-optics-lens",
    title: "Thin Lens & Image Formation",
    chapter: "Ray Optics",
    subject: "Physics",
    difficulty: "Beginner",
    minutes: 6,
    blurb: "Move the object and flip between real and virtual images.",
    gradient: "from-fuchsia-500/70 via-purple-500/45 to-indigo-400/30",
    popularity: 87,
    tags: ["lens", "mirror", "magnification", "focal length"],
    load: () => import("./sims/ray-optics-lens"),
  },
  {
    id: "wave-optics-ydse",
    title: "Young's Double Slit",
    chapter: "Wave Optics",
    subject: "Physics",
    difficulty: "Intermediate",
    minutes: 7,
    blurb: "Change λ, d and D to stretch the fringe pattern.",
    gradient: "from-amber-500/70 via-orange-500/45 to-rose-400/30",
    popularity: 84,
    tags: ["interference", "fringe width", "diffraction"],
    load: () => import("./sims/wave-optics-ydse"),
  },
  {
    id: "photoelectric-effect",
    title: "Photoelectric Effect",
    chapter: "Dual Nature of Matter",
    subject: "Physics",
    difficulty: "Intermediate",
    minutes: 7,
    blurb: "Cross the threshold frequency and read the stopping potential.",
    gradient: "from-emerald-500/70 via-teal-400/45 to-sky-400/30",
    popularity: 88,
    tags: ["photon", "work function", "stopping potential"],
    load: () => import("./sims/photoelectric-effect"),
  },
  {
    id: "radioactive-decay",
    title: "Radioactive Decay",
    chapter: "Nuclei",
    subject: "Physics",
    difficulty: "Beginner",
    minutes: 6,
    blurb: "Watch half-lives eat a sample exponentially.",
    gradient: "from-cyan-500/70 via-blue-500/45 to-indigo-400/30",
    popularity: 72,
    tags: ["half life", "decay constant", "activity"],
    load: () => import("./sims/radioactive-decay"),
  },
  {
    id: "semiconductor-diode",
    title: "p–n Junction Diode",
    chapter: "Semiconductors",
    subject: "Physics",
    difficulty: "Intermediate",
    minutes: 7,
    blurb: "Trace the I–V curve through forward and reverse bias.",
    gradient: "from-indigo-500/70 via-violet-500/45 to-sky-400/30",
    popularity: 70,
    tags: ["diode", "shockley", "rectifier", "knee voltage"],
    load: () => import("./sims/semiconductor-diode"),
  },
  {
    id: "atom-builder",
    title: "Build Your Own Atom",
    chapter: "Atomic Structure",
    subject: "Chemistry",
    difficulty: "Beginner",
    minutes: 7,
    blurb: "Add protons, neutrons and electrons and watch ions form.",
    gradient: "from-emerald-500/70 via-teal-400/45 to-sky-400/30",
    popularity: 95,
    tags: ["atom", "isotope", "ion", "nucleus"],
    load: () => import("./sims/atom-builder"),
  },
  {
    id: "electronic-configuration",
    title: "Electronic Configuration",
    chapter: "Atomic Structure",
    subject: "Chemistry",
    difficulty: "Intermediate",
    minutes: 7,
    blurb: "Fill orbitals by Aufbau, Hund and Pauli — visually.",
    gradient: "from-lime-500/70 via-emerald-400/45 to-cyan-400/30",
    popularity: 89,
    tags: ["orbital", "aufbau", "hund", "pauli"],
    load: () => import("./sims/electronic-configuration"),
  },
  {
    id: "chemical-bonding",
    title: "Chemical Bonding Lab",
    chapter: "Chemical Bonding",
    subject: "Chemistry",
    difficulty: "Intermediate",
    minutes: 8,
    blurb: "Join atoms and switch between ionic, covalent and metallic bonds.",
    gradient: "from-amber-500/70 via-orange-500/45 to-rose-400/30",
    popularity: 91,
    tags: ["ionic", "covalent", "metallic", "electronegativity"],
    load: () => import("./sims/chemical-bonding"),
  },
  {
    id: "hybridization",
    title: "Hybridization Mixer",
    chapter: "Chemical Bonding",
    subject: "Chemistry",
    difficulty: "Advanced",
    minutes: 8,
    blurb: "Mix orbitals and morph a molecule from linear to tetrahedral.",
    gradient: "from-emerald-500/70 via-teal-400/45 to-sky-400/30",
    popularity: 84,
    tags: ["sp", "sp2", "sp3", "bond angle"],
    load: () => import("./sims/hybridization"),
  },
  {
    id: "vsepr-geometry",
    title: "VSEPR 3D Geometry",
    chapter: "Chemical Bonding",
    subject: "Chemistry",
    difficulty: "Advanced",
    minutes: 8,
    blurb: "Rotate a molecule and watch lone pairs squeeze the bond angle.",
    gradient: "from-lime-500/70 via-emerald-400/45 to-cyan-400/30",
    popularity: 83,
    tags: ["vsepr", "lone pair", "geometry", "repulsion"],
    load: () => import("./sims/vsepr-geometry"),
  },
  {
    id: "states-of-matter",
    title: "States of Matter",
    chapter: "States of Matter",
    subject: "Chemistry",
    difficulty: "Beginner",
    minutes: 6,
    blurb: "Heat and cool particles through melting, boiling and sublimation.",
    gradient: "from-amber-500/70 via-orange-500/45 to-rose-400/30",
    popularity: 90,
    tags: ["phase", "melting", "boiling", "sublimation"],
    load: () => import("./sims/states-of-matter"),
  },
  {
    id: "thermochemistry",
    title: "Thermochemistry Lab",
    chapter: "Thermodynamics",
    subject: "Chemistry",
    difficulty: "Intermediate",
    minutes: 7,
    blurb: "Run exothermic and endothermic reactions and watch the thermometer.",
    gradient: "from-emerald-500/70 via-teal-400/45 to-sky-400/30",
    popularity: 81,
    tags: ["enthalpy", "exothermic", "endothermic", "calorimetry"],
    load: () => import("./sims/thermochemistry"),
  },
  {
    id: "chemical-equilibrium",
    title: "Chemical Equilibrium",
    chapter: "Equilibrium",
    subject: "Chemistry",
    difficulty: "Advanced",
    minutes: 9,
    blurb: "Add reactants, remove products and watch the equilibrium shift.",
    gradient: "from-lime-500/70 via-emerald-400/45 to-cyan-400/30",
    popularity: 86,
    tags: ["le chatelier", "kc", "shift", "reversible"],
    load: () => import("./sims/chemical-equilibrium"),
  },
  {
    id: "galvanic-cell",
    title: "Galvanic Cell Builder",
    chapter: "Electrochemistry",
    subject: "Chemistry",
    difficulty: "Advanced",
    minutes: 9,
    blurb: "Wire a Daniell cell and follow the electrons through the salt bridge.",
    gradient: "from-amber-500/70 via-orange-500/45 to-rose-400/30",
    popularity: 88,
    tags: ["nernst", "emf", "salt bridge", "electrode"],
    load: () => import("./sims/galvanic-cell"),
  },
  {
    id: "electrolysis-lab",
    title: "Electrolysis Lab",
    chapter: "Electrochemistry",
    subject: "Chemistry",
    difficulty: "Intermediate",
    minutes: 7,
    blurb: "Pass current and deposit metal using Faraday's laws.",
    gradient: "from-emerald-500/70 via-teal-400/45 to-sky-400/30",
    popularity: 78,
    tags: ["faraday", "electrolysis", "deposition", "current"],
    load: () => import("./sims/electrolysis-lab"),
  },
  {
    id: "reaction-lab",
    title: "Reaction Mixing Lab",
    chapter: "Practical Chemistry",
    subject: "Chemistry",
    difficulty: "Beginner",
    minutes: 7,
    blurb: "Mix reagents, heat them and observe colour, gas and precipitate.",
    gradient: "from-lime-500/70 via-emerald-400/45 to-cyan-400/30",
    popularity: 93,
    tags: ["precipitate", "gas", "colour", "catalyst"],
    load: () => import("./sims/reaction-lab"),
  },
  {
    id: "titration-lab",
    title: "Titration Lab",
    chapter: "Ionic Equilibrium",
    subject: "Chemistry",
    difficulty: "Intermediate",
    minutes: 8,
    blurb: "Drip titrant from the burette and catch the indicator flip.",
    gradient: "from-amber-500/70 via-orange-500/45 to-rose-400/30",
    popularity: 87,
    tags: ["titration", "equivalence", "indicator", "ph curve"],
    load: () => import("./sims/titration-lab"),
  },
  {
    id: "ph-indicator",
    title: "pH & Indicators",
    chapter: "Ionic Equilibrium",
    subject: "Chemistry",
    difficulty: "Beginner",
    minutes: 6,
    blurb: "Change acidity and watch indicators change colour.",
    gradient: "from-emerald-500/70 via-teal-400/45 to-sky-400/30",
    popularity: 80,
    tags: ["ph", "indicator", "ka", "acid base"],
    load: () => import("./sims/ph-indicator"),
  },
  {
    id: "buffer-solution",
    title: "Buffer Solution Lab",
    chapter: "Ionic Equilibrium",
    subject: "Chemistry",
    difficulty: "Intermediate",
    minutes: 7,
    blurb: "See a buffer resist pH change using Henderson–Hasselbalch.",
    gradient: "from-lime-500/70 via-emerald-400/45 to-cyan-400/30",
    popularity: 76,
    tags: ["buffer", "henderson", "pka", "salt"],
    load: () => import("./sims/buffer-solution"),
  },
  {
    id: "flame-test",
    title: "Virtual Flame Test",
    chapter: "Qualitative Analysis",
    subject: "Chemistry",
    difficulty: "Beginner",
    minutes: 5,
    blurb: "Burn metal salts and identify them by flame colour.",
    gradient: "from-amber-500/70 via-orange-500/45 to-rose-400/30",
    popularity: 85,
    tags: ["flame", "emission", "metal ion", "wavelength"],
    load: () => import("./sims/flame-test"),
  },
  {
    id: "crystal-growth",
    title: "Crystal Growth",
    chapter: "Solutions",
    subject: "Chemistry",
    difficulty: "Beginner",
    minutes: 6,
    blurb: "Cool a saturated solution and grow crystals.",
    gradient: "from-emerald-500/70 via-teal-400/45 to-sky-400/30",
    popularity: 70,
    tags: ["crystal", "solubility", "saturation", "nucleation"],
    load: () => import("./sims/crystal-growth"),
  },
  {
    id: "distillation-lab",
    title: "Distillation Setup",
    chapter: "Purification",
    subject: "Chemistry",
    difficulty: "Intermediate",
    minutes: 7,
    blurb: "Separate liquids by boiling point in a full distillation rig.",
    gradient: "from-lime-500/70 via-emerald-400/45 to-cyan-400/30",
    popularity: 72,
    tags: ["distillation", "volatility", "condenser", "purification"],
    load: () => import("./sims/distillation-lab"),
  },
  {
    id: "chromatography-lab",
    title: "Chromatography Lab",
    chapter: "Purification",
    subject: "Chemistry",
    difficulty: "Beginner",
    minutes: 6,
    blurb: "Separate a mixture on a TLC plate and compute Rf.",
    gradient: "from-amber-500/70 via-orange-500/45 to-rose-400/30",
    popularity: 71,
    tags: ["chromatography", "rf", "tlc", "polarity"],
    load: () => import("./sims/chromatography-lab"),
  },
  {
    id: "salt-analysis",
    title: "Salt Analysis Lab",
    chapter: "Qualitative Analysis",
    subject: "Chemistry",
    difficulty: "Advanced",
    minutes: 9,
    blurb: "Test unknown salts with reagents and identify the cation.",
    gradient: "from-emerald-500/70 via-teal-400/45 to-sky-400/30",
    popularity: 79,
    tags: ["cation", "precipitate", "group reagent", "analysis"],
    load: () => import("./sims/salt-analysis"),
  },
  {
    id: "filtration-lab",
    title: "Filtration Lab",
    chapter: "Purification",
    subject: "Chemistry",
    difficulty: "Beginner",
    minutes: 5,
    blurb: "Separate residue and filtrate through a funnel and filter paper.",
    gradient: "from-lime-500/70 via-emerald-400/45 to-cyan-400/30",
    popularity: 66,
    tags: ["filtration", "residue", "filtrate", "separation"],
    load: () => import("./sims/filtration-lab"),
  },
  {
    id: "sn1-reaction",
    title: "SN1 Reaction Mechanism",
    chapter: "Haloalkanes",
    subject: "Chemistry",
    difficulty: "Advanced",
    minutes: 9,
    blurb: "Form a carbocation and watch racemisation happen.",
    gradient: "from-amber-500/70 via-orange-500/45 to-rose-400/30",
    popularity: 92,
    tags: ["sn1", "carbocation", "racemisation", "solvolysis"],
    load: () => import("./sims/sn1-reaction"),
  },
  {
    id: "sn2-reaction",
    title: "SN2 Reaction Mechanism",
    chapter: "Haloalkanes",
    subject: "Chemistry",
    difficulty: "Advanced",
    minutes: 9,
    blurb: "Backside attack, pentavalent transition state and inversion.",
    gradient: "from-emerald-500/70 via-teal-400/45 to-sky-400/30",
    popularity: 94,
    tags: ["sn2", "inversion", "nucleophile", "steric"],
    load: () => import("./sims/sn2-reaction"),
  },
  {
    id: "e1-elimination",
    title: "E1 Elimination",
    chapter: "Haloalkanes",
    subject: "Chemistry",
    difficulty: "Advanced",
    minutes: 8,
    blurb: "Lose a leaving group then a proton to reach the Zaitsev alkene.",
    gradient: "from-lime-500/70 via-emerald-400/45 to-cyan-400/30",
    popularity: 77,
    tags: ["e1", "zaitsev", "carbocation", "elimination"],
    load: () => import("./sims/e1-elimination"),
  },
  {
    id: "e2-elimination",
    title: "E2 Elimination",
    chapter: "Haloalkanes",
    subject: "Chemistry",
    difficulty: "Advanced",
    minutes: 8,
    blurb: "Anti-periplanar concerted elimination — Zaitsev vs Hofmann.",
    gradient: "from-amber-500/70 via-orange-500/45 to-rose-400/30",
    popularity: 78,
    tags: ["e2", "hofmann", "anti periplanar", "base"],
    load: () => import("./sims/e2-elimination"),
  },
  {
    id: "addition-reaction",
    title: "Addition to Alkenes",
    chapter: "Hydrocarbons",
    subject: "Chemistry",
    difficulty: "Intermediate",
    minutes: 7,
    blurb: "Add HX across a double bond, Markovnikov or peroxide effect.",
    gradient: "from-emerald-500/70 via-teal-400/45 to-sky-400/30",
    popularity: 82,
    tags: ["markovnikov", "peroxide", "alkene", "addition"],
    load: () => import("./sims/addition-reaction"),
  },
  {
    id: "oxidation-reduction-organic",
    title: "Organic Oxidation & Reduction",
    chapter: "Alcohols & Aldehydes",
    subject: "Chemistry",
    difficulty: "Intermediate",
    minutes: 7,
    blurb: "Climb alcohol → aldehyde → acid and reduce back down.",
    gradient: "from-lime-500/70 via-emerald-400/45 to-cyan-400/30",
    popularity: 74,
    tags: ["oxidation", "reduction", "alcohol", "aldehyde"],
    load: () => import("./sims/oxidation-reduction-organic"),
  },
  {
    id: "venn-sets",
    title: "Interactive Venn Diagram",
    chapter: "Sets",
    subject: "Mathematics",
    difficulty: "Beginner",
    minutes: 5,
    blurb: "Animate union, intersection and complement live.",
    gradient: "from-cyan-500/70 via-blue-500/45 to-indigo-400/30",
    popularity: 84,
    tags: ["sets", "union", "intersection", "complement"],
    load: () => import("./sims/venn-sets"),
  },
  {
    id: "relations-mapping",
    title: "Relations & Mappings",
    chapter: "Relations and Functions",
    subject: "Mathematics",
    difficulty: "Beginner",
    minutes: 6,
    blurb: "Draw arrows between sets and classify one-one, onto and into.",
    gradient: "from-violet-500/70 via-indigo-500/45 to-blue-400/30",
    popularity: 78,
    tags: ["relation", "one one", "onto", "mapping"],
    load: () => import("./sims/relations-mapping"),
  },
  {
    id: "function-machine",
    title: "Function Machine",
    chapter: "Relations and Functions",
    subject: "Mathematics",
    difficulty: "Beginner",
    minutes: 5,
    blurb: "Drop an input in the machine and watch the output appear.",
    gradient: "from-rose-500/70 via-pink-500/45 to-violet-400/30",
    popularity: 86,
    tags: ["function", "domain", "range", "input output"],
    load: () => import("./sims/function-machine"),
  },
  {
    id: "quadratic-trajectory",
    title: "Quadratic Ball Trajectory",
    chapter: "Quadratic Equations",
    subject: "Mathematics",
    difficulty: "Beginner",
    minutes: 6,
    blurb: "Throw a ball and read the roots where it lands.",
    gradient: "from-cyan-500/70 via-blue-500/45 to-indigo-400/30",
    popularity: 88,
    tags: ["quadratic", "roots", "vertex", "parabola"],
    load: () => import("./sims/quadratic-trajectory"),
  },
  {
    id: "complex-argand",
    title: "Argand Plane Rotations",
    chapter: "Complex Numbers",
    subject: "Mathematics",
    difficulty: "Intermediate",
    minutes: 7,
    blurb: "Rotate vectors and see multiplication as rotation by i.",
    gradient: "from-violet-500/70 via-indigo-500/45 to-blue-400/30",
    popularity: 83,
    tags: ["complex", "argand", "modulus", "argument"],
    load: () => import("./sims/complex-argand"),
  },
  {
    id: "matrix-transform",
    title: "Matrix Transformations",
    chapter: "Matrices",
    subject: "Mathematics",
    difficulty: "Intermediate",
    minutes: 7,
    blurb: "Rotate, reflect, shear and scale a shape with a 2×2 matrix.",
    gradient: "from-rose-500/70 via-pink-500/45 to-violet-400/30",
    popularity: 85,
    tags: ["matrix", "determinant", "rotation", "transformation"],
    load: () => import("./sims/matrix-transform"),
  },
  {
    id: "permutation-combination",
    title: "Permutations & Combinations",
    chapter: "Permutations and Combinations",
    subject: "Mathematics",
    difficulty: "Intermediate",
    minutes: 7,
    blurb: "Seat students on chairs, linear and circular, and count arrangements.",
    gradient: "from-cyan-500/70 via-blue-500/45 to-indigo-400/30",
    popularity: 81,
    tags: ["npr", "ncr", "circular", "arrangement"],
    load: () => import("./sims/permutation-combination"),
  },
  {
    id: "pascal-binomial",
    title: "Pascal Triangle Builder",
    chapter: "Binomial Theorem",
    subject: "Mathematics",
    difficulty: "Beginner",
    minutes: 6,
    blurb: "Build the triangle row by row and pull out any term.",
    gradient: "from-violet-500/70 via-indigo-500/45 to-blue-400/30",
    popularity: 76,
    tags: ["binomial", "pascal", "general term", "coefficient"],
    load: () => import("./sims/pascal-binomial"),
  },
  {
    id: "sequence-series",
    title: "Sequences as Domino Chains",
    chapter: "Sequences and Series",
    subject: "Mathematics",
    difficulty: "Beginner",
    minutes: 6,
    blurb: "Watch AP and GP patterns grow and sum up.",
    gradient: "from-rose-500/70 via-pink-500/45 to-violet-400/30",
    popularity: 75,
    tags: ["ap", "gp", "sum", "series"],
    load: () => import("./sims/sequence-series"),
  },
  {
    id: "limits-zoom",
    title: "Limits by Zooming In",
    chapter: "Limits",
    subject: "Mathematics",
    difficulty: "Intermediate",
    minutes: 7,
    blurb: "Zoom into a point and approach it from both sides.",
    gradient: "from-cyan-500/70 via-blue-500/45 to-indigo-400/30",
    popularity: 80,
    tags: ["limit", "lhl", "rhl", "approach"],
    load: () => import("./sims/limits-zoom"),
  },
  {
    id: "continuity-road",
    title: "Continuity: Smooth vs Broken Road",
    chapter: "Continuity",
    subject: "Mathematics",
    difficulty: "Intermediate",
    minutes: 6,
    blurb: "Drive over jumps and holes to feel discontinuity.",
    gradient: "from-violet-500/70 via-indigo-500/45 to-blue-400/30",
    popularity: 73,
    tags: ["continuity", "jump", "removable", "discontinuity"],
    load: () => import("./sims/continuity-road"),
  },
  {
    id: "derivative-car",
    title: "Derivative as Instantaneous Speed",
    chapter: "Differentiation",
    subject: "Mathematics",
    difficulty: "Intermediate",
    minutes: 7,
    blurb: "A car on a curved road grows its own tangent line.",
    gradient: "from-rose-500/70 via-pink-500/45 to-violet-400/30",
    popularity: 87,
    tags: ["derivative", "tangent", "speed", "slope"],
    load: () => import("./sims/derivative-car"),
  },
  {
    id: "maxima-minima-coaster",
    title: "Roller Coaster Maxima & Minima",
    chapter: "Application of Derivatives",
    subject: "Mathematics",
    difficulty: "Advanced",
    minutes: 8,
    blurb: "Ride the curve through maxima, minima and inflection.",
    gradient: "from-cyan-500/70 via-blue-500/45 to-indigo-400/30",
    popularity: 82,
    tags: ["maxima", "minima", "inflection", "optimisation"],
    load: () => import("./sims/maxima-minima-coaster"),
  },
  {
    id: "integration-tank",
    title: "Integration as a Filling Tank",
    chapter: "Integrals",
    subject: "Mathematics",
    difficulty: "Intermediate",
    minutes: 7,
    blurb: "Fill a tank and watch the area under the rate curve grow.",
    gradient: "from-violet-500/70 via-indigo-500/45 to-blue-400/30",
    popularity: 84,
    tags: ["integration", "area", "accumulation", "definite"],
    load: () => import("./sims/integration-tank"),
  },
  {
    id: "differential-equations",
    title: "Differential Equations in Life",
    chapter: "Differential Equations",
    subject: "Mathematics",
    difficulty: "Advanced",
    minutes: 8,
    blurb: "Population growth, Newton cooling and decay in one lab.",
    gradient: "from-rose-500/70 via-pink-500/45 to-violet-400/30",
    popularity: 74,
    tags: ["growth", "cooling", "decay", "ode"],
    load: () => import("./sims/differential-equations"),
  },
  {
    id: "vectors-river-boat",
    title: "Vectors: Boat & Wind",
    chapter: "Vector Algebra",
    subject: "Mathematics",
    difficulty: "Intermediate",
    minutes: 7,
    blurb: "Cross a river and fly through wind using resultants.",
    gradient: "from-cyan-500/70 via-blue-500/45 to-indigo-400/30",
    popularity: 79,
    tags: ["vector", "resultant", "drift", "relative"],
    load: () => import("./sims/vectors-river-boat"),
  },
  {
    id: "geometry-3d-solids",
    title: "3D Solids & Sections",
    chapter: "Three Dimensional Geometry",
    subject: "Mathematics",
    difficulty: "Advanced",
    minutes: 8,
    blurb: "Rotate solids and slice them with a plane.",
    gradient: "from-violet-500/70 via-indigo-500/45 to-blue-400/30",
    popularity: 77,
    tags: ["3d", "plane", "section", "solid"],
    load: () => import("./sims/geometry-3d-solids"),
  },
  {
    id: "straight-line-intersection",
    title: "Road Intersection Simulator",
    chapter: "Straight Lines",
    subject: "Mathematics",
    difficulty: "Beginner",
    minutes: 6,
    blurb: "Cross two roads and read the angle between them.",
    gradient: "from-rose-500/70 via-pink-500/45 to-violet-400/30",
    popularity: 72,
    tags: ["line", "slope", "intersection", "angle"],
    load: () => import("./sims/straight-line-intersection"),
  },
  {
    id: "circle-tangent",
    title: "Rolling Wheel: Tangents & Chords",
    chapter: "Circles",
    subject: "Mathematics",
    difficulty: "Intermediate",
    minutes: 6,
    blurb: "Roll a wheel and track tangents, chords and their lengths.",
    gradient: "from-cyan-500/70 via-blue-500/45 to-indigo-400/30",
    popularity: 75,
    tags: ["circle", "tangent", "chord", "radius"],
    load: () => import("./sims/circle-tangent"),
  },
  {
    id: "ellipse-orbit",
    title: "Planet Orbit Ellipse",
    chapter: "Conic Sections",
    subject: "Mathematics",
    difficulty: "Intermediate",
    minutes: 7,
    blurb: "Watch Kepler's equal areas sweep on an ellipse.",
    gradient: "from-violet-500/70 via-indigo-500/45 to-blue-400/30",
    popularity: 86,
    tags: ["ellipse", "kepler", "focus", "eccentricity"],
    load: () => import("./sims/ellipse-orbit"),
  },
  {
    id: "parabola-dish",
    title: "Satellite Dish Parabola",
    chapter: "Conic Sections",
    subject: "Mathematics",
    difficulty: "Intermediate",
    minutes: 6,
    blurb: "Reflect parallel rays into the focus of a dish.",
    gradient: "from-rose-500/70 via-pink-500/45 to-violet-400/30",
    popularity: 81,
    tags: ["parabola", "focus", "directrix", "reflection"],
    load: () => import("./sims/parabola-dish"),
  },
  {
    id: "hyperbola-radar",
    title: "Radar Hyperbola",
    chapter: "Conic Sections",
    subject: "Mathematics",
    difficulty: "Advanced",
    minutes: 7,
    blurb: "Constant path difference traces a hyperbola between stations.",
    gradient: "from-cyan-500/70 via-blue-500/45 to-indigo-400/30",
    popularity: 70,
    tags: ["hyperbola", "asymptote", "path difference", "locus"],
    load: () => import("./sims/hyperbola-radar"),
  },
  {
    id: "probability-dice",
    title: "Dice, Coins & Spinner",
    chapter: "Probability",
    subject: "Mathematics",
    difficulty: "Beginner",
    minutes: 6,
    blurb: "Roll, toss and spin thousands of trials in seconds.",
    gradient: "from-violet-500/70 via-indigo-500/45 to-blue-400/30",
    popularity: 90,
    tags: ["probability", "dice", "coin", "distribution"],
    load: () => import("./sims/probability-dice"),
  },
  {
    id: "statistics-histogram",
    title: "Live Histogram & Mean Balance",
    chapter: "Statistics",
    subject: "Mathematics",
    difficulty: "Beginner",
    minutes: 6,
    blurb: "Balance the mean on a see-saw and find the median.",
    gradient: "from-rose-500/70 via-pink-500/45 to-violet-400/30",
    popularity: 73,
    tags: ["mean", "median", "variance", "histogram"],
    load: () => import("./sims/statistics-histogram"),
  },
  {
    id: "heart-circulation",
    title: "Human Heart — Blood Circulation",
    chapter: "Body Fluids & Circulation",
    subject: "Biology",
    difficulty: "Beginner",
    minutes: 7,
    blurb: "Watch blood loop through chambers and valves as the heart beats in real time.",
    gradient: "from-rose-500/70 via-pink-500/45 to-indigo-400/30",
    popularity: 80,
    tags: ["heart", "circulation", "neet", "cardiac output", "valves"],
    load: () => import("./sims/heart-circulation"),
  },
  {
    id: "neuron-action-potential",
    title: "Neuron — Action Potential",
    chapter: "Neural Control & Coordination",
    subject: "Biology",
    difficulty: "Intermediate",
    minutes: 8,
    blurb: "Stimulate a neuron and watch the all-or-none impulse race down the axon.",
    gradient: "from-violet-500/70 via-indigo-500/45 to-sky-400/30",
    popularity: 78,
    tags: ["neuron", "action potential", "neet", "axon", "membrane"],
    load: () => import("./sims/neuron-action-potential"),
  },
  {
    id: "respiratory-gas-exchange",
    title: "Respiratory System — Breathing & Gas Exchange",
    chapter: "Breathing & Exchange of Gases",
    subject: "Biology",
    difficulty: "Beginner",
    minutes: 7,
    blurb: "Drive the diaphragm and follow O₂ and CO₂ across the alveolar membrane.",
    gradient: "from-sky-500/70 via-cyan-400/45 to-emerald-400/30",
    popularity: 76,
    tags: ["respiration", "alveoli", "neet", "diaphragm", "gas exchange"],
    load: () => import("./sims/respiratory-gas-exchange"),
  },
  {
    id: "dna-replication",
    title: "DNA Replication",
    chapter: "Molecular Basis of Inheritance",
    subject: "Biology",
    difficulty: "Intermediate",
    minutes: 9,
    blurb: "Unzip the helix and build leading and lagging strands, Okazaki fragments included.",
    gradient: "from-violet-500/70 via-fuchsia-500/45 to-sky-400/30",
    popularity: 79,
    tags: ["dna", "replication", "neet", "okazaki", "semi-conservative"],
    load: () => import("./sims/dna-replication"),
  },
  {
    id: "mitosis-cell-division",
    title: "Mitosis — Cell Division",
    chapter: "Cell Cycle & Cell Division",
    subject: "Biology",
    difficulty: "Beginner",
    minutes: 8,
    blurb: "Step through prophase to cytokinesis and watch chromatids travel to the poles.",
    gradient: "from-rose-500/70 via-orange-400/45 to-amber-300/30",
    popularity: 77,
    tags: ["mitosis", "cell cycle", "neet", "anaphase", "cytokinesis"],
    load: () => import("./sims/mitosis-cell-division"),
  },
];

export const SIM_BY_ID = new Map(SIMULATIONS.map((s) => [s.id, s]));

export const SIM_SUBJECTS = ["All", "Physics", "Chemistry", "Mathematics", "Biology"] as const;
export type SimFilter = (typeof SIM_SUBJECTS)[number];

export function searchSimulations(query: string, subject: SimFilter): SimMeta[] {
  const q = query.trim().toLowerCase();
  return SIMULATIONS.filter((s) => {
    if (subject !== "All" && s.subject !== subject) return false;
    if (!q) return true;
    return (
      s.title.toLowerCase().includes(q) ||
      s.chapter.toLowerCase().includes(q) ||
      s.subject.toLowerCase().includes(q) ||
      s.tags.some((t) => t.includes(q))
    );
  });
}

export const SUBJECT_TINT: Record<SimSubject, string> = {
  Physics: "text-sky-300 border-sky-400/30 bg-sky-500/12",
  Chemistry: "text-emerald-300 border-emerald-400/30 bg-emerald-500/12",
  Mathematics: "text-violet-300 border-violet-400/30 bg-violet-500/12",
  Biology: "text-rose-300 border-rose-400/30 bg-rose-500/12",
};

export const DIFFICULTY_TINT: Record<SimDifficulty, string> = {
  Beginner: "text-emerald-300",
  Intermediate: "text-amber-300",
  Advanced: "text-rose-300",
};